import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { notificationService, type Notification } from '@/services/notificationService';
import Button from './Button';
import Toast, { type ToastTone } from './Toast';
import { cx } from './cx';

export interface ToastHostProps {
  className?: string;
}

const TONE: Record<string, ToastTone> = { success: 'success', error: 'danger', warning: 'warning', info: 'info' };

/** Renders notificationService toasts on <body>, above every modal layer. Mounted once by BaseLayout. */
export default function ToastHost({ className = '' }: ToastHostProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => notificationService.subscribe(setNotifications), []);

  if (notifications.length === 0) return null;

  // On <body> above every modal layer, so feedback raised from inside a modal
  // (e.g. "OTP sent") is never hidden behind it.
  return createPortal(
    <div className={cx('fixed right-4 top-4 z-toast flex w-full max-w-[min(30rem,calc(100vw-2rem))] flex-col gap-3', className)}>
      {notifications.map((n) => (
        <Toast
          key={n.id}
          tone={TONE[n.type] ?? 'info'}
          title={n.title}
          message={n.message && n.message.trim() ? n.message.trim() : n.type === 'error' ? 'An error occurred' : 'Notification'}
          onDismiss={() => notificationService.dismiss(n.id)}
          dismissLabel="Dismiss notification"
          className={cx(
            'w-full transition-all duration-(--k-dur-slow) ease-(--k-ease)',
            n.dismissed ? 'translate-x-full scale-95 opacity-0' : 'translate-x-0 scale-100 opacity-100'
          )}
          actions={
            n.actions && n.actions.length > 0
              ? n.actions.map((action, i) => (
                  <Button
                    key={i}
                    size="sm"
                    variant={action.style === 'primary' ? 'primary' : action.style === 'danger' ? 'danger' : 'secondary'}
                    onClick={action.action}
                  >
                    {action.label}
                  </Button>
                ))
              : undefined
          }
        />
      ))}
    </div>,
    document.body
  );
}
